import React, { useState, useEffect, useMemo, useRef } from "react";
import { api } from "../lib/api";
import { apiErr } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { UploadCloud, Sparkles, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, ArrowRight, Building2, Ban, Wand2, HelpCircle, Layers, ArrowDownWideNarrow } from "lucide-react";
import { toast } from "sonner";
import { ImportConfirmDialog } from "./ImportConfirmDialog";
import { SKIP_LABELS, REASON_HELP, flagExplanation } from "../lib/importReasons";

const conf = (c) => (c >= 0.8 ? "success" : c >= 0.5 ? "warning" : "danger");

// One secondary held-chip per row (besides the special Blocked block), in the same
// precedence as the backend _skip_key / importReasons.heldReason (R11/R13).
const HELD_CHIP = {
  not_calculated: { cls: "text-slate-300", label: "Not calculated", tid: "import-notcalc", icon: true },
  duplicate: { cls: "text-[#FFB800]", label: "⚠ Duplicate", tid: "import-dup" },
  fx_review: { cls: "text-[#FFB800]", label: "⚠ FX review", tid: "import-fx" },
  negative_qty: { cls: "text-[#FFB800]", label: "⚠ Negative", tid: "import-negative" },
  magnitude_outlier: { cls: "text-[#FFB800]", label: "⚠ Unusually large", tid: "import-magnitude" },
  boundary_review: { cls: "text-[#FFB800]", label: "⚠ Leased-asset boundary", tid: "import-boundary" },
  derived_total: { cls: "text-red-400", label: "Σ Derived total", tid: "import-derived" },
  rollup_annual: { cls: "text-sky-400", label: "Σ Annual roll-up", tid: "import-rollup" },
  low_confidence: { cls: "text-accent", label: "◷ Review", tid: "import-lowconf" },
  not_esg: { cls: "text-muted-foreground", label: "Not ESG", tid: "import-notesg" },
};
const secReason = (r) => (r.blocked ? null
  : r.duplicate ? "duplicate"
  : r.not_calculated ? "not_calculated"
  : r.fx_review ? "fx_review"
  : r.negative_qty ? "negative_qty"
  : r.magnitude_outlier ? "magnitude_outlier"
  : r.boundary_review ? "boundary_review"
  : r.derived_total ? "derived_total"
  : r.rollup_annual ? "rollup_annual"
  : r.low_confidence ? "low_confidence"
  : !r.is_esg ? "not_esg"
  : null);

// GHG Protocol Scope 3 categories (1–15) for the per-line suggestion editor.
const S3_CATS = ["Purchased goods & services", "Capital goods", "Fuel- & energy-related activities",
  "Upstream transport & distribution", "Waste generated in operations", "Business travel",
  "Employee commuting", "Upstream leased assets", "Downstream transport & distribution",
  "Processing of sold products", "Use of sold products", "End-of-life treatment",
  "Downstream leased assets", "Franchises", "Investments"];

// Estimated tCO₂e for a review row (used by the priority queue ordering).
const estTonnes = (r, factorMap) => {
  const f = r.factor_code ? factorMap[r.factor_code] : null;
  const kg = f && f.factor_kgco2e != null ? f.factor_kgco2e : 0;
  // Spend-based (EEIO) factors are kgCO2e per SGD — use the FX-converted reporting amount.
  const v = f && f.basis === "spend"
    ? (r.amount_reporting != null ? Number(r.amount_reporting) : (r.value === "" || r.value == null ? 0 : Number(r.value)))
    : (r.value === "" || r.value == null ? 0 : Number(r.value));
  return (Number.isFinite(v) ? v : 0) * kg / 1000;
};

// Priority rank: blockers first, then other flagged rows, then ready, then non-ESG.
const priorityRank = (r) => {
  if (r.blocked) return 0;
  if (r.is_esg && (r.not_calculated || r.duplicate || r.derived_total || r.rollup_annual || r.low_confidence)) return 1;
  if (r.is_esg) return 2;
  return 3;
};

const orderRows = (list, priorityOrder, factorMap) => {
  if (!priorityOrder) return list;
  return [...list].sort((a, b) => (priorityRank(a) - priorityRank(b)) || (estTonnes(b, factorMap) - estTonnes(a, factorMap)));
};

const RULE_OF = (r) => {
  const k = ["blocked", "duplicate", "not_calculated", "derived_total", "rollup_annual", "low_confidence"].find((x) => r[x]);
  return SKIP_LABELS[k] || "Ready";
};
const GROUP_FNS = {
  rule: RULE_OF,
  supplier: (r) => r.supplier || "Unknown supplier",
  category: (r) => r.category || "Uncategorised",
};

// Bulk accept/reject toolbar — group the review rows by rule / supplier / category,
// then include or exclude a whole group in one click. Blocked rows are never
// bulk-included (they still need their typed acknowledgement).
function BulkReviewBar({ rows, groupBy, onBulk }) {
  const groups = useMemo(() => {
    const fn = GROUP_FNS[groupBy] || RULE_OF;
    const m = {};
    rows.forEach((r) => { const g = fn(r); (m[g] = m[g] || []).push(r); });
    return Object.entries(m).sort((a, b) => b[1].length - a[1].length);
  }, [rows, groupBy]);
  if (!rows.length) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap text-[11px]" data-testid="import-bulk-bar">
      {groups.map(([g, rs]) => {
        const includable = rs.filter((r) => !r.blocked).map((r) => r.idx);
        const allIdx = rs.map((r) => r.idx);
        const slug = g.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
        return (
          <div key={g} className="inline-flex items-center gap-1 rounded border border-border bg-muted/30 px-1.5 py-0.5" data-testid={`bulk-group-${slug}`}>
            <span className="text-foreground max-w-[160px] truncate" title={g}>{g}</span>
            <span className="text-muted-foreground">({rs.length})</span>
            <button type="button" onClick={() => onBulk(includable, true)} disabled={!includable.length} title={`Include all ${g}`} data-testid={`bulk-include-${slug}`}
              className="rounded px-1 text-[#00E65B] hover:bg-[#00E65B]/15 disabled:opacity-30">✓</button>
            <button type="button" onClick={() => onBulk(allIdx, false)} title={`Exclude all ${g}`} data-testid={`bulk-exclude-${slug}`}
              className="rounded px-1 text-red-400 hover:bg-red-500/15">✕</button>
          </div>
        );
      })}
    </div>
  );
}

// Controls strip above a review table: priority-order toggle + group-by picker + bulk bar.
function ReviewControls({ rows, priorityOrder, setPriorityOrder, groupBy, setGroupBy, onBulk }) {
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap mb-1.5" data-testid="import-review-controls">
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => setPriorityOrder((v) => !v)} data-testid="import-priority-toggle"
          className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] font-medium transition-colors ${priorityOrder ? "border-accent/50 bg-accent/10 text-accent" : "border-border text-muted-foreground hover:text-foreground"}`}
          title="Priority order shows blockers first, then the lines that move your total the most (by tCO₂e). Toggle off for file order.">
          <ArrowDownWideNarrow className="h-3 w-3" /> {priorityOrder ? "Priority order" : "File order"}
        </button>
        <div className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <Layers className="h-3 w-3" /> Bulk by
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="h-7 w-28 text-xs" data-testid="import-groupby-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="rule">Rule</SelectItem>
              <SelectItem value="supplier">Supplier</SelectItem>
              <SelectItem value="category">Category</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <BulkReviewBar rows={rows} groupBy={groupBy} onBulk={onBulk} />
    </div>
  );
}


// Compact per-line "Why?" disclosure — plain-language rule / evidence / fix for a flagged row.
function FlagExplain({ r }) {
  const ex = flagExplanation(r);
  if (!ex) return null;
  return (
    <details className="mt-0.5 text-[9px] leading-tight" data-testid={`import-flag-explain-${r.idx}`}>
      <summary className="cursor-pointer text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"><HelpCircle className="h-2.5 w-2.5" /> Why? / Fix</summary>
      <div className="mt-0.5 space-y-0.5 pl-2 border-l border-border">
        <div><span className="font-semibold text-foreground">Rule:</span> <span className="text-muted-foreground">{ex.rule}</span></div>
        <div><span className="font-semibold text-foreground">Evidence:</span> <span className="text-muted-foreground">{ex.evidence}</span></div>
        <div><span className="font-semibold text-[#00E65B]">Fix:</span> <span className="text-muted-foreground">{ex.fix}</span></div>
      </div>
    </details>
  );
}

const ORIGIN_OPTS = [
  { v: "measured", l: "Measured" },
  { v: "calculated", l: "Calculated" },
  { v: "estimate", l: "Estimate" },
  { v: "declaration", l: "Declaration" },
  { v: "ai_inferred", l: "AI-inferred" },
];

// Keep the dialog open when the user interacts with a portaled Radix Select / toast.
const keepOpenOnPortal = (e) => {
  const t = e?.detail?.originalEvent?.target;
  if (t && t.closest && t.closest('[data-radix-popper-content-wrapper],[data-sonner-toaster],[role="listbox"]')) {
    e.preventDefault();
  }
};

export function RowEditor({ r, factors, factorMap, facilities = [], onChange, onConvert }) {
  const isEnv = r.pillar === "Environment";
  const fac = r.factor_code ? factorMap[r.factor_code] : null;
  const rowBg = r.blocked ? "bg-red-500/10" : r.duplicate ? "bg-[#FFB800]/5" : r.not_calculated ? "bg-slate-500/10" : r.derived_total ? "bg-red-500/5" : r.rollup_annual ? "bg-sky-500/5" : r.low_confidence ? "bg-accent/5" : "";
  return (
    <tr className={`border-b border-border align-top ${rowBg}`} data-testid={`import-row-${r.idx}`}>
      <td className="p-1.5"><input type="checkbox" checked={r.include} onChange={(e) => onChange(r.idx, "include", e.target.checked)} className="h-3.5 w-3.5 accent-[hsl(var(--accent))]" data-testid={`import-include-${r.idx}`} /></td>
      <td className="p-1.5 max-w-[220px]">
        <div className="text-[11px] truncate text-muted-foreground" title={Object.values(r.raw).join(" · ")}>{Object.values(r.raw).slice(0, 2).join(" · ")}</div>
        {r.blocked && (
          <div className="mt-0.5">
            <span className="inline-flex items-center gap-0.5 text-[8px] font-bold text-red-400 uppercase tracking-wide" data-testid={`import-blocked-${r.idx}`}><Ban className="h-2.5 w-2.5" /> Blocked</span>
            <div className="text-[9px] text-red-300/90 leading-tight mt-0.5" data-testid={`import-blocked-reason-${r.idx}`}>{r.blocked_reason}</div>
            {r.blocked_convertible && onConvert && (
              <button type="button" onClick={() => onConvert(r.idx)} data-testid={`import-convert-${r.idx}`}
                className="mt-0.5 inline-flex items-center gap-1 rounded border border-accent/40 bg-accent/10 px-1.5 py-0.5 text-[9px] font-medium text-accent hover:bg-accent/20 transition-colors">
                <Wand2 className="h-2.5 w-2.5" /> Convert to {r.suggested_value != null ? `${r.suggested_value} ` : ""}{r.suggested_unit}
              </button>
            )}
          </div>
        )}
        {!r.blocked && !r.duplicate && r.not_calculated && (
          <span className="inline-flex items-center gap-0.5 text-[8px] font-bold text-slate-300 uppercase tracking-wide" data-testid={`import-notcalc-${r.idx}`} title="No emission factor or computable result — imported, not calculated. Never shown as a verified emissions figure."><HelpCircle className="h-2.5 w-2.5" /> Not calculated</span>
        )}
        {(() => {
          const sec = secReason(r);
          if (!sec || sec === "not_calculated") return null;
          const c = HELD_CHIP[sec];
          return <span className={`inline-block text-[8px] font-bold uppercase tracking-wide ${c.cls}`} data-testid={`${c.tid}-${r.idx}`} title={r.reason || REASON_HELP[sec]}>{c.label}</span>;
        })()}
        {r.scope_corrected && <span className="inline-block text-[8px] font-bold text-sky-400 uppercase tracking-wide ml-1" data-testid={`import-scopefix-${r.idx}`} title="Scope corrected to 1 — owned on-site combustion equipment is Scope 1 under the GHG Protocol, even if the source file labels it Scope 3 / Cat 13">⚑ Scope→1</span>}
        {r.boundary_auto && <span className="inline-block text-[8px] font-bold text-sky-400 uppercase tracking-wide ml-1" data-testid={`import-boundary-auto-${r.idx}`} title={r.reason || "Leased-asset boundary auto-classified from your operational-control setting"}>⚑ {r.boundary_auto === "scope2" ? "Leased→S2" : "Leased→S3.8"}</span>}
        {r.fx_status === "ready" && r.currency_original && r.amount_reporting != null && (
          <span className="inline-block text-[8px] font-medium text-[#00E65B] ml-1" data-testid={`import-fx-converted-${r.idx}`} title={`${r.currency_original} ${r.value} → ${r.amount_reporting} SGD at ${r.fx_rate} on ${r.fx_rate_date}`}>≈ {r.amount_reporting} SGD</span>
        )}
        <FlagExplain r={r} />
      </td>
      <td className="p-1.5">
        <Select value={r.pillar || "none"} onValueChange={(v) => onChange(r.idx, "pillar", v === "none" ? null : v)}>
          <SelectTrigger className="h-7 w-28 text-xs" data-testid={`import-pillar-${r.idx}`}><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent><SelectItem value="none">—</SelectItem><SelectItem value="Environment">Environment</SelectItem><SelectItem value="Social">Social</SelectItem><SelectItem value="Governance">Governance</SelectItem></SelectContent>
        </Select>
      </td>
      <td className="p-1.5"><Input value={r.category || ""} onChange={(e) => onChange(r.idx, "category", e.target.value)} className="h-7 w-40 text-xs" placeholder="Category" title={r.category || ""} /></td>
      <td className="p-1.5"><Input value={r.activity_type || ""} onChange={(e) => onChange(r.idx, "activity_type", e.target.value)} className="h-7 w-48 text-xs" placeholder="Activity" title={r.activity_type || ""} /></td>
      <td className="p-1.5"><Input type="number" step="any" value={r.value ?? ""} onChange={(e) => onChange(r.idx, "value", e.target.value)} className="h-7 w-20 text-xs" /></td>
      <td className="p-1.5"><Input value={r.unit || ""} onChange={(e) => onChange(r.idx, "unit", e.target.value)} className="h-7 w-16 text-xs" /></td>
      <td className="p-1.5"><Input value={r.period || ""} onChange={(e) => onChange(r.idx, "period", e.target.value)} className="h-7 w-20 text-xs" placeholder="YYYY-MM / Qn" /></td>
      <td className="p-1.5">
        <Select value={r.facility || "none"} onValueChange={(v) => onChange(r.idx, "facility", v === "none" ? null : v)}>
          <SelectTrigger className={`h-7 w-40 text-xs ${r.include && !r.facility ? "border-[#FFB800]/60 text-[#FFB800]" : ""}`} title={r.facility || r.facility_raw || "Unassigned"} data-testid={`import-facility-${r.idx}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
          <SelectContent className="max-h-56"><SelectItem value="none">Unassigned</SelectItem>{facilities.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}{r.facility_raw && !facilities.includes(r.facility_raw) && <SelectItem value={r.facility_raw}>As in file: {r.facility_raw}</SelectItem>}</SelectContent>
        </Select>
      </td>
      <td className="p-1.5">
        {isEnv ? (
          <div className="space-y-0.5">
            <Select value={r.scope != null ? String(r.scope) : "none"} onValueChange={(v) => onChange(r.idx, "scope", v === "none" ? null : Number(v))}>
              <SelectTrigger className="h-7 w-[88px] text-xs" data-testid={`import-scope-${r.idx}`}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No scope</SelectItem>
                <SelectItem value="1">Scope 1</SelectItem>
                <SelectItem value="2">Scope 2</SelectItem>
                <SelectItem value="3">Scope 3</SelectItem>
              </SelectContent>
            </Select>
            {r.scope === 3 && (
              <div className="space-y-0.5" data-testid={`import-s3-${r.idx}`}>
                <Select value={r.s3_cat ? String(r.s3_cat) : "none"} onValueChange={(v) => onChange(r.idx, "s3_cat", v === "none" ? null : Number(v))}>
                  <SelectTrigger className="h-6 w-[108px] text-[10px]" title={r.s3_reason || "AI-suggested Scope 3 category"} data-testid={`import-s3cat-${r.idx}`}><SelectValue placeholder="Cat —" /></SelectTrigger>
                  <SelectContent className="max-h-56"><SelectItem value="none">Cat —</SelectItem>{S3_CATS.map((c, i) => <SelectItem key={i + 1} value={String(i + 1)}>{`${i + 1}. ${c}`}</SelectItem>)}</SelectContent>
                </Select>
                {r.s3_method && <div className="text-[8px] uppercase tracking-wide text-muted-foreground" data-testid={`import-s3method-${r.idx}`} title={r.s3_reason || ""}>{r.s3_method}</div>}
              </div>
            )}
          </div>
        ) : <span className="text-[10px] text-muted-foreground">—</span>}
      </td>
      <td className="p-1.5">
        {isEnv ? (
          <div className="space-y-0.5">
            <Select value={r.factor_code || "none"} onValueChange={(v) => onChange(r.idx, "factor_code", v === "none" ? null : v)}>
              <SelectTrigger className="h-7 w-36 text-xs" title={r.factor_code || ""} data-testid={`import-factor-${r.idx}`}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="max-h-56">
                <SelectItem value="none">No factor</SelectItem>
                {[...factors].sort((a, b) => (r.is_monetary || r.spend_rescued ? ((b.basis === "spend") - (a.basis === "spend")) : 0)).map((f) => (
                  <SelectItem key={f.code} value={f.code}>{f.code}{f.basis === "spend" ? "  · spend" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(r.spend_rescued || r.is_monetary) && !r.factor_code && (
              <div className="text-[9px] leading-tight text-[#FFB800] max-w-[150px]" data-testid={`import-spend-nudge-${r.idx}`}>Pick a spend-based (EEIO) factor to calculate this purchase.</div>
            )}
            {fac && (
              <div className="text-[9px] leading-tight text-muted-foreground max-w-[150px]" data-testid={`import-factor-src-${r.idx}`} title={`${r.factor_code} · ${fac.name} · ${fac.factor_kgco2e} kgCO₂e/${fac.unit}`}>
                {fac.source}{fac.region ? ` · ${fac.region}` : ""}{fac.year ? ` · ${fac.year}` : ""}
              </div>
            )}
          </div>
        ) : <span className="text-[10px] text-muted-foreground">—</span>}
      </td>
      <td className="p-1.5"><Badge variant={conf(r.confidence)} className="text-[9px]" title={r.reason}>{Math.round((r.confidence || 0) * 100)}%</Badge></td>
    </tr>
  );
}

const COLS = ["✓", "Source row", "Pillar", "Category", "Activity", "Value", "Unit", "Period", "Site", "Scope", "Factor / source", "Conf."];
export const HeaderRow = () => (
  <tr className="border-b border-border">{COLS.map((c, i) => <th key={i} className="p-1.5 text-left text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{c}</th>)}</tr>
);

export function BulkAssignSite({ facilities, unassignedCount, onAssign }) {
  const [val, setVal] = useState("");
  if (!facilities.length) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs rounded-md border border-border bg-muted/30 px-2.5 py-1.5" data-testid="import-bulk-assign">
      <Building2 className="h-3.5 w-3.5 text-accent" />
      <span className="text-muted-foreground">Assign site to all Unassigned{unassignedCount ? ` (${unassignedCount})` : ""}:</span>
      <Select value={val} onValueChange={setVal}>
        <SelectTrigger className="h-7 w-48 text-xs" data-testid="import-bulk-site-select"><SelectValue placeholder="Choose a facility…" /></SelectTrigger>
        <SelectContent className="max-h-56">{facilities.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
      </Select>
      <Button variant="outline" size="sm" className="h-7 text-xs" disabled={!val || !unassignedCount} onClick={() => val && onAssign(val)} data-testid="import-bulk-assign-btn">Apply</Button>
    </div>
  );
}

function SheetBlock({ sheet, rows, factors, factorMap, facilities = [], onChange, onConvert, priorityOrder, setPriorityOrder, groupBy, setGroupBy, onBulk }) {
  const a = sheet.assessment || {};
  const accepted = (rows || []).filter((r) => r.is_esg);
  const excluded = (rows || []).filter((r) => !r.is_esg);
  const unassignedCount = accepted.filter((r) => r.include && !r.facility).length;
  const assignUnassigned = (fname) => accepted.forEach((r) => { if (r.include && !r.facility) onChange(sheet.sheet_name, r.idx, "facility", fname); });
  return (
    <div className="card-flat p-3 space-y-2" data-testid={`wb-sheet-${sheet.sheet_name}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4 text-accent" />
          <span className="font-medium text-sm">{sheet.sheet_name}</span>
          {sheet.used_memory && <Badge variant="default" className="text-[9px]">⚡ Memory</Badge>}
          {sheet.empty && <Badge variant="outline" className="text-[9px]">empty</Badge>}
        </div>
        <div className="flex gap-1.5 text-xs">
          <Badge variant="success">{a.accepted || 0} ESG</Badge>
          <Badge variant="outline">{a.rejected || 0} not ESG</Badge>
          {sheet.duplicate_count > 0 && <Badge variant="warning">{sheet.duplicate_count} dup</Badge>}
          {sheet.derived_total_count > 0 && <Badge variant="danger">{sheet.derived_total_count} derived</Badge>}
          {sheet.rollup_annual_count > 0 && <Badge variant="default" data-testid={`wb-rollup-${sheet.sheet_name}`}>{sheet.rollup_annual_count} roll-up</Badge>}
          {sheet.low_confidence_count > 0 && <Badge variant="default">{sheet.low_confidence_count} review</Badge>}
          {sheet.fx_review_count > 0 && <Badge variant="warning" data-testid={`wb-fx-${sheet.sheet_name}`}>{sheet.fx_review_count} FX review</Badge>}
          {sheet.negative_count > 0 && <Badge variant="warning" data-testid={`wb-negative-${sheet.sheet_name}`}>{sheet.negative_count} negative</Badge>}
          {sheet.magnitude_outlier_count > 0 && <Badge variant="warning" data-testid={`wb-magnitude-${sheet.sheet_name}`}>{sheet.magnitude_outlier_count} unusually large</Badge>}
          {sheet.boundary_review_count > 0 && <Badge variant="warning" data-testid={`wb-boundary-${sheet.sheet_name}`}>{sheet.boundary_review_count} leased-asset</Badge>}
          {sheet.truncated && <Badge variant="warning" data-testid={`wb-truncated-${sheet.sheet_name}`}>first {sheet.analyzed_rows} of {sheet.total_rows} rows</Badge>}
        </div>
      </div>
      {sheet.error && (
        <p className="text-[11px] text-red-400 font-medium" data-testid={`wb-sheet-error-${sheet.sheet_name}`}>⚠ {a.summary || "This sheet failed to analyze — its rows are still counted in the workbook total."}</p>
      )}
      {a.summary && !sheet.error && <p className="text-[11px] text-muted-foreground">{a.summary}</p>}
      {sheet.org_counts?.counts?.length > 0 && (
        <p className="text-[10px] text-muted-foreground" data-testid={`wb-orgcounts-${sheet.sheet_name}`}>
          Rows by {sheet.org_counts.column}: {sheet.org_counts.counts.map((c) => `${c.name} ${c.rows}`).join(" · ")} — {sheet.total_rows} rows in sheet. Verify these counts match your source exports before committing.
        </p>
      )}
      {accepted.length > 0 && (
        <div className="overflow-x-auto">
          {unassignedCount > 0 && <div className="mb-1.5"><BulkAssignSite facilities={facilities} unassignedCount={unassignedCount} onAssign={assignUnassigned} /></div>}
          <ReviewControls rows={accepted} priorityOrder={priorityOrder} setPriorityOrder={setPriorityOrder} groupBy={groupBy} setGroupBy={setGroupBy} onBulk={(idxs, inc) => onBulk(sheet.sheet_name, idxs, inc)} />
          <table className="w-full"><thead><HeaderRow /></thead>
            <tbody>{orderRows(accepted, priorityOrder, factorMap).map((r) => <RowEditor key={r.idx} r={r} factors={factors} factorMap={factorMap} facilities={facilities} onChange={(idx, k, v) => onChange(sheet.sheet_name, idx, k, v)} onConvert={(idx) => onConvert(sheet.sheet_name, idx)} />)}</tbody>
          </table>
        </div>
      )}
      {excluded.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">Excluded / Not ESG ({excluded.length}) — tick to force-include</summary>
          <div className="overflow-x-auto opacity-90 mt-1">
            <table className="w-full"><thead><HeaderRow /></thead>
              <tbody>{excluded.map((r) => <RowEditor key={r.idx} r={r} factors={factors} factorMap={factorMap} facilities={facilities} onChange={(idx, k, v) => onChange(sheet.sheet_name, idx, k, v)} onConvert={(idx) => onConvert(sheet.sheet_name, idx)} />)}</tbody>
            </table>
          </div>
        </details>
      )}
      {(rows || []).length === 0 && <p className="text-[11px] text-muted-foreground">No rows detected in this sheet.</p>}
    </div>
  );
}

export function AiImportDialog({ open, onClose, onImported, presetFormat = null, resumeImportId = null }) {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [stage, setStage] = useState("upload");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [factors, setFactors] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [done, setDone] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [targetTenant, setTargetTenant] = useState("");
  const [wbRows, setWbRows] = useState({});
  const [analyzeAll, setAnalyzeAll] = useState(true);
  const [wbProgress, setWbProgress] = useState(null);
  const [commitProg, setCommitProg] = useState(null);
  const [committedSheets, setCommittedSheets] = useState({});
  const [origin, setOrigin] = useState("measured");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sitelessConfirm, setSitelessConfirm] = useState(null);
  const [senseCheck, setSenseCheck] = useState(null);
  const [priorityOrder, setPriorityOrder] = useState(true);
  const [groupBy, setGroupBy] = useState("rule");
  const pollRef = useRef(null);
  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => () => stopPoll(), []);

  useEffect(() => {
    if (!open) { stopPoll(); return; }
    setStage("upload"); setFile(null); setResult(null); setRows([]); setDone(null);
    setWbRows({}); setAnalyzeAll(true);
    setWbProgress(null); setCommitProg(null); setCommittedSheets({}); setOrigin("measured"); setConfirmOpen(false); setSenseCheck(null);
    if (resumeImportId) { setStage("resuming"); resume(resumeImportId); }
    setTargetTenant(presetFormat?.tenant_id || "");
    api.get("/emissions/factors").then((r) => setFactors(r.data.factors)).catch(() => {});
    api.get("/facilities").then((r) => setFacilities((r.data.facilities || []).map((f) => f.name).filter(Boolean))).catch(() => setFacilities([]));
    if (isSuper) api.get("/tenants").then((r) => setTenants(r.data.tenants || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const factorMap = useMemo(() => Object.fromEntries(factors.map((f) => [f.code, f])), [factors]);
  const isExcel = file && /\.(xlsx|xls)$/i.test(file.name);
  const isCsv = file && /\.csv$/i.test(file.name);
  const useBackground = (isExcel && analyzeAll) || isCsv;

  const analyze = async (sheetName) => {
    if (!file) return;
    if (isSuper && !targetTenant) { toast.error("Select a target tenant first."); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      if (isSuper && targetTenant) fd.append("tenant_id", targetTenant);
      if (sheetName) fd.append("sheet", sheetName);
      const { data } = await api.post("/esg-import/analyze", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setResult(data);
      setRows(data.rows.map((r) => ({ ...r, include: r.is_esg && !r.duplicate && !r.low_confidence && !r.derived_total && !r.rollup_annual && !r.blocked && !r.not_calculated && !r.fx_review && !r.negative_qty && !r.magnitude_outlier && !r.boundary_review })));
      setStage("review");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const enterReview = (st) => {
    setResult(st);
    const map = {};
    (st.sheets || []).forEach((s) => { map[s.sheet_name] = (s.rows || []).map((r) => ({ ...r, include: r.is_esg && !r.duplicate && !r.low_confidence && !r.derived_total && !r.rollup_annual && !r.blocked && !r.not_calculated && !r.fx_review && !r.negative_qty && !r.magnitude_outlier && !r.boundary_review })); });
    setWbRows(map);
    setCommittedSheets({});
    setStage("review");
  };

  const startPoll = (importId) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const { data: st } = await api.get(`/esg-import/${importId}/workbook-status`);
        if (st.status === "analyzing") { setWbProgress(st.progress || null); return; }
        stopPoll();
        if (st.status === "failed") { toast.error(st.error || "Workbook analysis failed. Please re-upload."); setStage("upload"); return; }
        enterReview(st);
      } catch { /* transient poll error — keep polling */ }
    }, 3000);
  };

  const resume = async (importId) => {
    try {
      const { data: st } = await api.get(`/esg-import/${importId}/workbook-status`);
      if (st.status === "analyzing") {
        setResult({ import_id: importId, multi_sheet: true });
        setWbProgress(st.progress || null);
        setStage("analyzing");
        startPoll(importId);
      } else if (st.status === "failed") {
        toast.error(st.error || "This analysis failed — please re-upload the workbook.");
        onClose();
      } else if (st.status === "committed") {
        toast.info("This import has already been committed.");
        onClose();
      } else {
        enterReview(st);
      }
    } catch (e) { toast.error(apiErr(e)); onClose(); }
  };

  const analyzeWorkbook = async () => {
    if (!file) return;
    if (isSuper && !targetTenant) { toast.error("Select a target tenant first."); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      if (isSuper && targetTenant) fd.append("tenant_id", targetTenant);
      const { data } = await api.post("/esg-import/analyze-workbook", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 55000 });
      setResult(data);
      setWbProgress(data.progress || null);
      setStage("analyzing");
      setBusy(false);
      startPoll(data.import_id);
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  const update = (idx, k, v) => setRows((p) => p.map((r) => r.idx === idx ? { ...r, [k]: v } : r));
  const updateWb = (sheet, idx, k, v) => setWbRows((p) => ({ ...p, [sheet]: (p[sheet] || []).map((r) => r.idx === idx ? { ...r, [k]: v } : r) }));
  const bulkSet = (idxs, inc) => setRows((p) => p.map((r) => idxs.includes(r.idx) ? { ...r, include: inc } : r));
  const bulkSetWb = (sheet, idxs, inc) => setWbRows((p) => ({ ...p, [sheet]: (p[sheet] || []).map((r) => idxs.includes(r.idx) ? { ...r, include: inc } : r) }));
  const _convert = (r) => ({ ...r, unit: r.suggested_unit || r.unit, value: r.suggested_value ?? r.value, blocked: false, blocked_convertible: false, include: true });
  const convertRow = (idx) => setRows((p) => p.map((r) => r.idx === idx ? _convert(r) : r));
  const convertWb = (sheet, idx) => setWbRows((p) => ({ ...p, [sheet]: (p[sheet] || []).map((r) => r.idx === idx ? _convert(r) : r) }));

  const accepted = useMemo(() => rows.filter((r) => r.is_esg), [rows]);
  const excluded = useMemo(() => rows.filter((r) => !r.is_esg), [rows]);
  const mapRow = (r, ack) => ({ idx: r.idx, include: !!r.include, pillar: r.pillar, category: r.category, activity_type: r.activity_type, value: r.value === "" ? null : Number(r.value), unit: r.unit, period: r.period, scope: r.scope ?? null, factor_code: r.factor_code, facility: r.facility || null, reference: r.reference || null, supplier: r.supplier || null, s3_cat: r.s3_cat ?? null, s3_method: r.s3_method || null, override_ack: ack || null, origin: r.origin || null });
  const isWb = result?.multi_sheet;
  const includeCount = isWb
    ? Object.values(wbRows).flat().filter((r) => r.include && r.pillar && r.category).length
    : rows.filter((r) => r.include && r.pillar && r.category).length;
  const unassignedCount = isWb
    ? Object.values(wbRows).flat().filter((r) => r.include && r.pillar && r.category && !r.facility).length
    : rows.filter((r) => r.include && r.pillar && r.category && !r.facility).length;
  const reviewRows = useMemo(() => (isWb
    ? Object.entries(wbRows).flatMap(([sheet, rs]) => rs.map((r) => ({ ...r, __sheet: sheet })))
    : rows.map((r) => ({ ...r, __sheet: null }))), [isWb, wbRows, rows]);
  const buckets = useMemo(() => {
    let ready = 0, review = 0, blocked = 0;
    reviewRows.forEach((r) => {
      if (r.blocked) { blocked++; return; }
      if (r.include && r.pillar && r.category && !r.not_calculated) { ready++; return; }
      if (r.is_esg) review++;
    });
    return { ready, review, blocked };
  }, [reviewRows]);

  const commit = async (ackMap = {}, sitelessAck = false) => {
    // RD-08 — warn before committing site-less rows: if any included row has no matching
    // site, confirm company-wide import first (unless the reviewer already acknowledged).
    if (!sitelessAck && unassignedCount > 0) {
        const pool = isWb ? Object.values(wbRows).flat() : rows;
        const names = [...new Set(pool
          .filter((r) => r.include && r.pillar && r.category && !r.facility)
          .map((r) => r.facility_raw)
          .filter(Boolean))];
        setSitelessConfirm({ ackMap, count: unassignedCount, names });
        return;
    }
    setBusy(true);
    try {
      let data;
      if (isWb) {
        const entries = Object.entries(wbRows)
          .map(([sheet_name, rs]) => ({ sheet_name, rows: rs.map((r) => mapRow(r, ackMap[`${sheet_name}::${r.idx}`])) }))
          .filter((s) => s.rows.some((r) => r.include && r.pillar && r.category))
          .filter((s) => !committedSheets[s.sheet_name]);
        if (entries.length === 0) {
          ({ data } = await api.post(`/esg-import/${result.import_id}/commit-workbook`, { sheets: [], finalize: true, origin, siteless_ack: sitelessAck }, { timeout: 55000 }));
        }
        for (let i = 0; i < entries.length; i++) {
          setCommitProg({ done: i, total: entries.length, current: entries[i].sheet_name });
          const res = await api.post(`/esg-import/${result.import_id}/commit-workbook`,
            { sheets: [entries[i]], finalize: i === entries.length - 1, origin, siteless_ack: sitelessAck }, { timeout: 55000 });
          data = res.data;
          setCommittedSheets((p) => ({ ...p, [entries[i].sheet_name]: true }));
        }
        setCommitProg(null);
      } else {
        ({ data } = await api.post(`/esg-import/${result.import_id}/commit`, { rows: rows.map((r) => mapRow(r, ackMap[String(r.idx)])), origin, siteless_ack: sitelessAck }));
      }
      setDone(data); setStage("done");
      toast.success(`Imported ${data.committed} record${data.committed === 1 ? "" : "s"} as Unverified${data.tenant_name ? ` into ${data.tenant_name}` : ""}`);
      onImported && onImported();
    } catch (e) {
      setCommitProg(null);
      if (isWb) toast.error(`${apiErr(e)} — sheets already committed are saved; click Commit again to finish the remaining sheets.`);
      else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  // Open the Confirm & Reconcile gate, fetching a batch sense-check first (advisory).
  const openConfirm = async () => {
    setSenseCheck(null);
    setConfirmOpen(true);
    if (!result?.import_id) return;
    try {
      const included = isWb
        ? Object.entries(wbRows).flatMap(([sheet, rs]) => rs.map((r) => mapRow(r)))
        : rows.map((r) => mapRow(r));
      const { data } = await api.post(`/esg-import/${result.import_id}/sense-check`, { rows: included, origin });
      setSenseCheck(data);
    } catch { /* advisory only — ignore */ }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => {
      if (o) return;
      if (stage === "analyzing") toast.info("Analysis continues in the background — you'll get a notification when it's ready, and you can Resume review anytime from the Import Runs list.");
      onClose();
    }}>
      <DialogContent
        className="w-[96vw] max-w-[1400px] max-h-[92vh] overflow-y-auto"
        data-testid="ai-import-dialog"
        onPointerDownOutside={keepOpenOnPortal}
        onInteractOutside={keepOpenOnPortal}
        onFocusOutside={keepOpenOnPortal}
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Sparkles className="h-5 w-5 text-accent" /> AI ESG Bulk Import</DialogTitle>
        </DialogHeader>

        {stage === "upload" && (
          <div className="space-y-4">
            {presetFormat ? (
              <div className="card-flat p-3 border-accent/30 bg-accent/5" data-testid="import-preset-banner">
                <div className="inline-flex items-center gap-2 flex-wrap">
                  <Badge variant="default" className="text-[10px]">⚡ Re-run format</Badge>
                  <span className="text-sm font-medium">{presetFormat.detected_type || "Saved import format"}</span>
                  {presetFormat.learned_count > 0 && <Badge variant="success" className="text-[10px]">{presetFormat.learned_count} learned line items</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">Drop this period's file in the <span className="font-medium text-foreground">same format</span> — recognised rows auto-classify from memory instantly (fewer AI tokens). Only new line items are sent to the AI. If the columns differ, the AI re-analyzes the whole file.</p>
                {presetFormat.headers?.length > 0 && (
                  <p className="text-[11px] text-muted-foreground mt-1">Expected columns: <span className="font-mono text-foreground">{presetFormat.headers.join(", ")}</span></p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Drop any business file — ERP purchase ledgers, HR/payroll exports, utility bills, CSR extracts. The AI parser detects ESG-relevant rows, classifies them into Pillar → Category → Activity, assigns GHG Scope 1/2/3, suggests emission factors with their source, and flags non-ESG lines for your review.</p>
            )}
            {isSuper && (
              <div className="space-y-1.5" data-testid="import-tenant-picker">
                <label className="label-eyebrow text-[10px] inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> Target tenant <span className="text-red-500">*</span></label>
                <Select value={targetTenant || "none"} onValueChange={(v) => setTargetTenant(v === "none" ? "" : v)}>
                  <SelectTrigger className="w-full" data-testid="import-tenant-select"><SelectValue placeholder="Select which tenant this data belongs to" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">Select a tenant…</SelectItem>
                    {tenants.map((t) => <SelectItem key={t._id} value={t._id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">You're signed in as a platform Super Admin (no tenant of your own). Choose the tenant that should receive these records to avoid misattribution.</p>
              </div>
            )}
            <label className="block border-2 border-dashed border-border rounded-md p-10 text-center cursor-pointer hover:border-accent/50 transition-colors" data-testid="import-dropzone">
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="import-file-input" />
              <UploadCloud className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              {file ? <div className="text-sm font-medium inline-flex items-center gap-2"><FileSpreadsheet className="h-4 w-4 text-accent" /> {file.name}</div> : <div className="text-sm text-muted-foreground">Click to choose a CSV or Excel file</div>}
            </label>
            {isExcel && (
              <label className="flex items-start gap-2 text-xs cursor-pointer card-flat p-2.5" data-testid="import-allsheets-toggle">
                <input type="checkbox" checked={analyzeAll} onChange={(e) => setAnalyzeAll(e.target.checked)} className="h-3.5 w-3.5 mt-0.5 accent-[hsl(var(--accent))]" data-testid="import-allsheets-checkbox" />
                <span><span className="font-medium text-foreground">Import all sheets in this workbook</span> — analyze every tab of a consolidated ERP export in one pass (Purchase Orders, Invoices, Utilities, Waste…) and file them in a single commit. Uncheck to import just the first sheet.</span>
              </label>
            )}
            <Button variant="primary" className="w-full" onClick={() => (useBackground ? analyzeWorkbook() : analyze())} disabled={!file || busy || (isSuper && !targetTenant)} data-testid="import-analyze-btn">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> AI analyzing…</> : <><Sparkles className="h-4 w-4" /> {isExcel && analyzeAll ? "Analyze all sheets" : "Analyze with AI"}</>}
            </Button>
          </div>
        )}

        {stage === "resuming" && (
          <div className="py-14 text-center space-y-3" data-testid="import-resuming">
            <Loader2 className="h-8 w-8 animate-spin text-accent mx-auto" />
            <p className="text-sm text-muted-foreground">Loading your analyzed workbook…</p>
          </div>
        )}

        {stage === "analyzing" && result && (
          <div className="space-y-4 py-2" data-testid="import-analyzing">
            <div className="flex items-center justify-between flex-wrap gap-2 text-xs">
              {result.tenant_name && <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-accent" /> Importing into <span className="font-medium text-foreground">{result.tenant_name}</span></span>}
              <span className="text-muted-foreground inline-flex items-center gap-1.5" data-testid="import-analyze-progress-label">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" /> Analyzed {wbProgress?.done || 0} of {wbProgress?.total || result.sheet_count} sheets
              </span>
            </div>
            <div className="h-1.5 rounded bg-border overflow-hidden">
              <div className="h-full bg-[hsl(var(--accent))] transition-all duration-500" style={{ width: `${((wbProgress?.done || 0) / (wbProgress?.total || 1)) * 100}%` }} data-testid="import-analyze-progressbar" />
            </div>
            <div className="space-y-1.5 max-h-[50vh] overflow-y-auto" data-testid="import-analyze-sheets">
              {(wbProgress?.sheets || []).map((s) => (
                <div key={s.name} className="flex items-center justify-between card-flat px-3 py-2 text-xs" data-testid={`analyze-sheet-${s.name}`}>
                  <span className="inline-flex items-center gap-2">
                    {s.status === "done" && <CheckCircle2 className="h-3.5 w-3.5 text-[#00E65B]" />}
                    {s.status === "analyzing" && <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" />}
                    {s.status === "error" && <AlertTriangle className="h-3.5 w-3.5 text-red-400" />}
                    {s.status === "pending" && <span className="h-3.5 w-3.5 rounded-full border border-border inline-block" />}
                    <span className={s.status === "pending" ? "text-muted-foreground" : "font-medium"}>{s.name}</span>
                  </span>
                  <span className="text-muted-foreground">
                    {s.status === "done" ? `${s.accepted} ESG · ${s.total_rows} rows` : s.status === "analyzing" ? "AI analyzing…" : s.status === "error" ? "failed — shown with error in review" : "queued"}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">Large workbooks are analyzed in the background — a 10-sheet ERP export typically takes 1–3 minutes. Keep this dialog open; the review screen opens automatically when every sheet is done.</p>
          </div>
        )}

        {stage === "review" && result && (
          <div className="space-y-4">
            {(result.tenant_name || isWb || (result.sheet_names && result.sheet_names.length > 0)) && (
              <div className="flex items-center justify-between flex-wrap gap-2 text-xs" data-testid="import-context-bar">
                {result.tenant_name && <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-accent" /> Importing into <span className="font-medium text-foreground" data-testid="import-context-tenant">{result.tenant_name}</span></span>}
                {isWb ? (
                  <span className="inline-flex items-center gap-1.5 text-muted-foreground" data-testid="import-workbook-summary"><FileSpreadsheet className="h-3.5 w-3.5" /> {result.totals?.sheet_count} sheets · {result.totals?.total_rows != null ? `${result.totals.total_rows} rows detected · ` : ""}<span className="text-[#00E65B] font-medium">{result.totals?.accepted} ESG rows</span> across the workbook</span>
                ) : (result.sheet_names && result.sheet_names.length > 0 && (
                  <div className="inline-flex items-center gap-2">
                    <span className="text-muted-foreground">Sheet:</span>
                    {result.sheet_names.length > 1 ? (
                      <>
                        <Select value={result.analyzed_sheet || result.sheet_names[0]} onValueChange={(v) => analyze(v)}>
                          <SelectTrigger className="h-7 w-44 text-xs" data-testid="import-sheet-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="max-h-72">{result.sheet_names.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                        <span className="text-[10px] text-muted-foreground">({result.sheet_names.length} sheets — switch to import another)</span>
                      </>
                    ) : <span className="font-medium text-foreground">{result.analyzed_sheet || result.sheet_names[0]}</span>}
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap" data-testid="import-buckets">
              <span className="label-eyebrow text-[10px] text-muted-foreground mr-1">Staging &amp; Review</span>
              <Badge variant="success" className="text-[10px] inline-flex items-center gap-1" data-testid="bucket-ready"><CheckCircle2 className="h-3 w-3" /> Ready to commit · {buckets.ready}</Badge>
              <Badge variant="warning" className="text-[10px] inline-flex items-center gap-1" data-testid="bucket-review"><AlertTriangle className="h-3 w-3" /> Needs review · {buckets.review}</Badge>
              <Badge variant="danger" className="text-[10px] inline-flex items-center gap-1" data-testid="bucket-blocked"><Ban className="h-3 w-3" /> Blocked · {buckets.blocked}</Badge>
              {buckets.blocked > 0 && <span className="text-[11px] text-red-400">Blocked lines are a genuine stop — apply the safe conversion or correct them before they can commit.</span>}
            </div>

            {isWb ? (
              <div className="space-y-3" data-testid="import-workbook-review">
                {(result.sheets || []).map((s) => <SheetBlock key={s.sheet_name} sheet={s} rows={wbRows[s.sheet_name]} factors={factors} factorMap={factorMap} facilities={facilities} onChange={updateWb} onConvert={convertWb} priorityOrder={priorityOrder} setPriorityOrder={setPriorityOrder} groupBy={groupBy} setGroupBy={setGroupBy} onBulk={bulkSetWb} />)}
              </div>
            ) : (
              <>
                {/* Assessment */}
                <div className="card-flat p-3" data-testid="import-assessment">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="inline-flex items-center gap-2">
                      <Badge variant={result.used_ai ? "success" : "warning"} className="text-[10px]">{result.used_ai ? "AI classified" : "Heuristic"}</Badge>
                      {result.used_memory && <Badge variant="default" className="text-[10px]" data-testid="import-memory-badge">⚡ Memory ({result.learned_count})</Badge>}
                      <span className="text-sm font-medium">Detected: {result.assessment.detected_type}</span>
                    </div>
                    <div className="flex gap-2 text-xs">
                      <Badge variant="success">{result.assessment.accepted} ESG</Badge>
                      <Badge variant="outline">{result.assessment.rejected} not ESG</Badge>
                      {result.duplicate_count > 0 && <Badge variant="warning" data-testid="import-dup-badge">{result.duplicate_count} duplicate{result.duplicate_count === 1 ? "" : "s"}</Badge>}
                      {result.derived_total_count > 0 && <Badge variant="danger" data-testid="import-derived-badge">{result.derived_total_count} derived total{result.derived_total_count === 1 ? "" : "s"}</Badge>}
                      {result.rollup_annual_count > 0 && <Badge variant="default" data-testid="import-rollup-badge">{result.rollup_annual_count} annual roll-up{result.rollup_annual_count === 1 ? "" : "s"}</Badge>}
                      {result.low_confidence_count > 0 && <Badge variant="default" data-testid="import-lowconf-badge">{result.low_confidence_count} held for review</Badge>}
                      {result.fx_review_count > 0 && <Badge variant="warning" data-testid="import-fx-badge">{result.fx_review_count} FX review</Badge>}
                      {result.negative_count > 0 && <Badge variant="warning" data-testid="import-negative-badge">{result.negative_count} negative</Badge>}
                      {result.magnitude_outlier_count > 0 && <Badge variant="warning" data-testid="import-magnitude-badge">{result.magnitude_outlier_count} unusually large</Badge>}
                      {result.boundary_review_count > 0 && <Badge variant="warning" data-testid="import-boundary-badge">{result.boundary_review_count} leased-asset boundary</Badge>}
                      {result.factor_aligned_count > 0 && <Badge variant="outline" data-testid="import-aligned-badge">{result.factor_aligned_count} factor aligned</Badge>}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">{result.assessment.summary}</p>
                  {result.org_counts?.counts?.length > 0 && (
                    <p className="text-[10px] text-muted-foreground mt-1" data-testid="import-orgcounts">
                      Rows by {result.org_counts.column}: {result.org_counts.counts.map((c) => `${c.name} ${c.rows}`).join(" · ")} — {result.total_rows} rows in file. Verify these counts match your source exports before committing.
                    </p>
                  )}
                  {result.used_memory && <p className="text-[11px] text-accent mt-1">⚡ This file format was recognised from a previous upload — rows were auto-classified instantly. Only new line items were sent to the AI.</p>}
                  {result.low_confidence_count > 0 && <p className="text-[11px] text-accent mt-1 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {result.low_confidence_count} row(s) below {Math.round((result.conf_threshold || 0.6) * 100)}% confidence are unticked and held for your review — confirm and re-tick to import.</p>}
                  {result.duplicate_count > 0 && <p className="text-[11px] text-[#FFB800] mt-1 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {result.duplicate_count} row(s) are duplicates (same document / reference number + posting period — either repeated within this file or already in the inventory) and are unticked to avoid double-counting — open "Why? / Fix" on a row for detail, or re-tick to import anyway.</p>}
                  {result.derived_total_count > 0 && <p className="text-[11px] text-red-400 mt-1 inline-flex items-center gap-1" data-testid="import-derived-note"><AlertTriangle className="h-3 w-3" /> {result.derived_total_count} row(s) look like pre-calculated emission totals and are unticked — importing them as activity data would double-count emissions.</p>}
                  {result.rollup_annual_count > 0 && <p className="text-[11px] text-sky-400 mt-1 inline-flex items-center gap-1" data-testid="import-rollup-note"><AlertTriangle className="h-3 w-3" /> {result.rollup_annual_count} whole-year (annual) roll-up row(s) sit alongside the monthly detail for the same metric and are unticked to avoid double-counting the yearly total — re-tick to import an annual figure deliberately.</p>}
                  {result.fx_review_count > 0 && <p className="text-[11px] text-[#FFB800] mt-1 inline-flex items-center gap-1" data-testid="import-fx-note"><AlertTriangle className="h-3 w-3" /> {result.fx_review_count} foreign-currency row(s) are held — their in-file FX rate is missing, zero, or dated before the posting month. Supply a valid rate dated in or after the posting month, then re-tick.</p>}
                  {result.negative_count > 0 && <p className="text-[11px] text-[#FFB800] mt-1 inline-flex items-center gap-1" data-testid="import-negative-note"><AlertTriangle className="h-3 w-3" /> {result.negative_count} row(s) have a negative quantity and are held — a genuine credit note or a data error. Re-tick to commit a legitimate credit.</p>}
                  {result.magnitude_outlier_count > 0 && <p className="text-[11px] text-[#FFB800] mt-1 inline-flex items-center gap-1" data-testid="import-magnitude-note"><AlertTriangle className="h-3 w-3" /> {result.magnitude_outlier_count} row(s) are unusually large for their activity (an order-of-magnitude outlier) and are held for review — re-tick if the figure is correct.</p>}
                  {result.boundary_review_count > 0 && <p className="text-[11px] text-[#FFB800] mt-1 inline-flex items-center gap-1" data-testid="import-boundary-note"><AlertTriangle className="h-3 w-3" /> {result.boundary_review_count} landlord-supplied / leased-asset energy row(s) are held for a Scope-boundary review — confirm Scope 2 vs Scope 3 Category 8 under your operational-control approach, then re-tick.</p>}
                  {result.factor_aligned_count > 0 && <p className="text-[11px] text-muted-foreground mt-1 inline-flex items-center gap-1" data-testid="import-aligned-note"><CheckCircle2 className="h-3 w-3 text-[#00E65B]" /> {result.factor_aligned_count} row(s) had their emission factor aligned to the one already used for that item, so totals stay consistent across imports.</p>}
                  {result.truncated && <p className="text-[11px] text-[#FFB800] mt-1 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Large file: analyzed first {result.max_rows} of {result.total_rows} rows.</p>}
                </div>

                {/* Accepted */}
                <div>
                  <div className="label-eyebrow mb-1.5 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-[#00E65B]" /> Accepted ESG data ({accepted.length})</div>
                  {accepted.some((r) => r.include && !r.facility) && (
                    <div className="mb-1.5"><BulkAssignSite facilities={facilities} unassignedCount={accepted.filter((r) => r.include && !r.facility).length} onAssign={(fname) => setRows((p) => p.map((r) => (r.is_esg && r.include && !r.facility ? { ...r, facility: fname } : r)))} /></div>
                  )}
                  <ReviewControls rows={accepted} priorityOrder={priorityOrder} setPriorityOrder={setPriorityOrder} groupBy={groupBy} setGroupBy={setGroupBy} onBulk={bulkSet} />
                  <div className="card-flat overflow-x-auto">
                    <table className="w-full"><thead><HeaderRow /></thead>
                      <tbody>{orderRows(accepted, priorityOrder, factorMap).map((r) => <RowEditor key={r.idx} r={r} factors={factors} factorMap={factorMap} facilities={facilities} onChange={update} onConvert={convertRow} />)}</tbody>
                    </table>
                  </div>
                </div>

                {/* Excluded */}
                {excluded.length > 0 && (
                  <div>
                    <div className="label-eyebrow mb-1.5 inline-flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-muted-foreground" /> Excluded / Not ESG ({excluded.length}) — tick to force-include</div>
                    <div className="card-flat overflow-x-auto opacity-90">
                      <table className="w-full"><thead><HeaderRow /></thead>
                        <tbody>{excluded.map((r) => <RowEditor key={r.idx} r={r} factors={factors} factorMap={factorMap} facilities={facilities} onChange={update} onConvert={convertRow} />)}</tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="flex items-center justify-between pt-1 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setStage("upload")}>Back</Button>
                <div className="inline-flex items-center gap-1.5" data-testid="import-origin-picker">
                  <span className="text-[11px] text-muted-foreground">Evidence origin</span>
                  <Select value={origin} onValueChange={setOrigin}>
                    <SelectTrigger className="w-36 h-8 text-xs" data-testid="import-origin-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ORIGIN_OPTS.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`import-origin-${o.v}`}>{o.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap justify-end">
                {unassignedCount > 0 && facilities.length > 0 && (
                  <span className="text-[11px] text-[#FFB800] inline-flex items-center gap-1" data-testid="import-unassigned-warning">
                    <Building2 className="h-3 w-3" /> {unassignedCount} row{unassignedCount === 1 ? "" : "s"} have no site — they'll import as Unassigned. Assign a site above, or commit as-is for company-wide figures.
                  </span>
                )}
                <Button variant="primary" onClick={openConfirm} disabled={busy || includeCount === 0} data-testid="import-commit-btn">
                  {busy ? (commitProg ? <><Loader2 className="h-4 w-4 animate-spin" /> Committing sheet {commitProg.done + 1}/{commitProg.total} — {commitProg.current}</> : <><Loader2 className="h-4 w-4 animate-spin" /> Committing…</>) : <>Review &amp; commit {includeCount} record{includeCount === 1 ? "" : "s"} <ArrowRight className="h-4 w-4" /></>}
                </Button>
              </div>
            </div>
          </div>
        )}

        {stage === "done" && done && (
          <div className="py-6 text-center space-y-3" data-testid="import-done">
            <CheckCircle2 className="h-12 w-12 text-[#00E65B] mx-auto" />
            <div className="font-display text-2xl font-bold">{done.committed} records imported</div>
            {done.tenant_name && <div className="text-sm text-muted-foreground" data-testid="import-done-tenant">into <span className="font-medium text-foreground">{done.tenant_name}</span></div>}
            {done.committed > 0 && <div className="flex justify-center"><Badge variant="outline" className="text-[10px] border-[#FFB800]/40 text-[#FFB800]" data-testid="import-done-unverified">Landed as Unverified — verify later from Data Management</Badge></div>}
            <div className="flex justify-center gap-2 text-xs">
              {Object.entries(done.by_pillar).filter(([, n]) => n > 0).map(([p, n]) => <Badge key={p} variant="outline">{p}: {n}</Badge>)}
            </div>
            {done.sheets_summary?.length > 0 && (
              <div className="max-w-md mx-auto text-left space-y-1" data-testid="import-done-sheets">
                <div className="label-eyebrow text-[10px]">Per-sheet breakdown</div>
                {done.sheets_summary.map((s) => (
                  <div key={s.sheet_name} className="flex items-center justify-between text-xs card-flat px-2.5 py-1.5">
                    <span className="inline-flex items-center gap-1.5"><FileSpreadsheet className="h-3 w-3 text-accent" /> {s.sheet_name}</span>
                    <span className="text-muted-foreground">{s.committed} of {s.accepted} committed</span>
                  </div>
                ))}
              </div>
            )}
            {done.created_categories?.length > 0 && <p className="text-xs text-muted-foreground">New taxonomy categories added: {done.created_categories.join(", ")}</p>}
            {done.skipped_count > 0 && (
              <div className="max-w-md mx-auto text-left card-flat p-3" data-testid="import-skipped-report">
                <div className="text-xs font-semibold text-[#FFB800] mb-1.5">
                  {done.committed} of {done.source_rows || done.committed + done.skipped_count} reviewed rows committed — {done.skipped_count} skipped
                </div>
                <div className="flex flex-wrap gap-1 mb-1.5">
                  {Object.entries(done.skipped_by_reason || {}).map(([k, n]) => (
                    <Badge key={k} variant="outline" className="text-[9px]">{SKIP_LABELS[k] || k}: {n}</Badge>
                  ))}
                </div>
                {(done.skipped_rows || []).length > 0 && (
                  <details className="text-[11px] text-muted-foreground" data-testid="import-skipped-details">
                    <summary className="cursor-pointer hover:text-foreground">Show skipped rows</summary>
                    <div className="mt-1.5 space-y-0.5 max-h-44 overflow-y-auto pr-1">
                      {done.skipped_rows.map((s, i) => (
                        <div key={i} className="flex gap-2">
                          <span className="font-mono shrink-0">#{(s.idx ?? 0) + 1}{s.sheet ? ` (${s.sheet})` : ""}</span>
                          <span className="truncate flex-1">{s.label || "—"}</span>
                          <span className="shrink-0 text-[#FFB800]">{SKIP_LABELS[s.reason] || s.reason}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                <p className="text-[10px] text-muted-foreground mt-1.5">Nothing is dropped silently — resume this run from Import Runs to force-include any skipped row, then commit again.</p>
              </div>
            )}
            <Button variant="primary" onClick={onClose} data-testid="import-close-btn">Done</Button>
          </div>
        )}
        <ImportConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          rows={reviewRows}
          senseCheck={senseCheck}
          totalRows={isWb ? (result?.totals?.total_rows ?? reviewRows.length) : (result?.total_rows ?? rows.length)}
          committing={busy}
          onConfirm={(ackMap) => { setConfirmOpen(false); commit(ackMap); }}
        />
        {/* RD-08 — confirm before committing site-less (Unassigned) rows company-wide */}
        <Dialog open={!!sitelessConfirm} onOpenChange={(o) => { if (!o) setSitelessConfirm(null); }}>
          <DialogContent className="max-w-md" data-testid="siteless-confirm-dialog">
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Building2 className="h-4 w-4 text-[#FFB800]" /> Commit rows without a site?</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">
              {sitelessConfirm?.count} row{sitelessConfirm?.count === 1 ? "" : "s"} couldn't be matched to one of your sites and will import as <span className="font-medium text-foreground">Unassigned</span> (counted company-wide). Assign a site first for per-facility figures, or confirm to commit as-is.
            </p>
            {sitelessConfirm?.names?.length > 0 && (
              <div className="card-flat p-2.5 text-[11px] text-muted-foreground max-h-28 overflow-y-auto" data-testid="siteless-names">
                <span className="text-foreground/80">Unmatched in file:</span> {sitelessConfirm.names.join(", ")}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setSitelessConfirm(null)} data-testid="siteless-cancel-btn">Go back &amp; assign</Button>
              <Button variant="primary" onClick={() => { const sc = sitelessConfirm; setSitelessConfirm(null); commit(sc.ackMap, true); }} data-testid="siteless-confirm-btn">Commit as-is (company-wide)</Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

